import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";

import { api } from "@/lib/api";
import { googleAuthUrl, googleRedirectUri } from "@/lib/googleAuth";
import { setStoredToken } from "@/types";

type ErrorKind = "expired" | "access_denied" | "server" | "network" | "generic";

interface ErrorInfo {
  kind: ErrorKind;
  raw: string;
}

function classifyError(message: string): ErrorKind {
  const m = message.toLowerCase();
  if (m.includes("invalid_grant")) return "expired";
  if (m.includes("access_denied")) return "access_denied";
  if (m.includes("failed to fetch") || m.includes("networkerror") || m.includes("load failed")) return "network";
  if (/\b(5\d\d|server|worker|exception)\b/.test(m)) return "server";
  return "generic";
}

function ErrorCard({
  icon,
  iconBg,
  iconColor,
  title,
  description,
  primary,
  secondary,
}: {
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  title: string;
  description: string;
  primary?: React.ReactNode;
  secondary?: React.ReactNode;
}) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-surface-secondary px-4">
      <div className="w-full max-w-sm rounded-card bg-white p-8 shadow-card text-center">
        <div className={`mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full ${iconBg}`}>
          <svg className={`h-6 w-6 ${iconColor}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            {icon}
          </svg>
        </div>
        <h2 className="text-base font-semibold text-near-black">{title}</h2>
        <p className="mt-2 text-sm text-text-secondary leading-relaxed">{description}</p>
        {primary && <div className="mt-6">{primary}</div>}
        {secondary && <div className="mt-3">{secondary}</div>}
      </div>
    </div>
  );
}

const GOOGLE_ICON = (
  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

function RetryButton() {
  return (
    <a
      href={googleAuthUrl()}
      className="flex w-full items-center justify-center gap-2 rounded-btn bg-rausch py-2.5 text-sm font-semibold text-white hover:bg-[#e0334f] transition-colors"
    >
      {GOOGLE_ICON}
      Sign in with Google
    </a>
  );
}

function BackLink() {
  return (
    <Link
      to="/login"
      className="block text-sm text-text-secondary hover:text-near-black transition-colors"
    >
      ← Back to sign in
    </Link>
  );
}

function ExpiredErrorView() {
  return (
    <ErrorCard
      iconBg="bg-amber-50"
      iconColor="text-amber-500"
      icon={<path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />}
      title="Sign-in link expired"
      description="Google sign-in links are single-use and expire quickly. Please start a new sign-in."
      primary={<RetryButton />}
      secondary={<BackLink />}
    />
  );
}

function AccessDeniedView() {
  return (
    <ErrorCard
      iconBg="bg-amber-50"
      iconColor="text-amber-500"
      icon={<path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />}
      title="Access not granted"
      description="You cancelled the sign-in or didn't grant the required permissions. You can try again whenever you're ready."
      primary={<RetryButton />}
      secondary={<BackLink />}
    />
  );
}

function ServerErrorView() {
  return (
    <ErrorCard
      iconBg="bg-rose-50"
      iconColor="text-error-text"
      icon={<path strokeLinecap="round" strokeLinejoin="round" d="M5.25 14.25h13.5m-13.5 0a3 3 0 01-3-3m3 3a3 3 0 100 6h13.5a3 3 0 100-6m-16.5-3a3 3 0 013-3h13.5a3 3 0 013 3m-19.5 0a4.5 4.5 0 01.9-2.7L5.737 5.1a3.375 3.375 0 012.7-1.35h7.126c1.062 0 2.062.5 2.7 1.35l2.587 3.45a4.5 4.5 0 01.9 2.7m0 0a3 3 0 01-3 3m0 3h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008zm-3 6h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008z" />}
      title="Something went wrong"
      description="We hit a snag on our end while completing your sign-in. Please try again — this is usually temporary."
      primary={<RetryButton />}
      secondary={<BackLink />}
    />
  );
}

function NetworkErrorView() {
  return (
    <ErrorCard
      iconBg="bg-rose-50"
      iconColor="text-error-text"
      icon={<path strokeLinecap="round" strokeLinejoin="round" d="M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12.53 18.22l-.53.53-.53-.53a.75.75 0 011.06 0z" />}
      title="Connection problem"
      description="We couldn't reach our servers to complete your sign-in. Check your connection and try again."
      primary={<RetryButton />}
      secondary={<BackLink />}
    />
  );
}

function GenericErrorView() {
  return (
    <ErrorCard
      iconBg="bg-rose-50"
      iconColor="text-error-text"
      icon={<path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />}
      title="Google sign-in failed"
      description="We couldn't complete your sign-in with Google. Please try again or use your email and password instead."
      primary={<RetryButton />}
      secondary={<BackLink />}
    />
  );
}

const ERROR_VIEWS: Record<ErrorKind, React.ComponentType> = {
  expired: ExpiredErrorView,
  access_denied: AccessDeniedView,
  server: ServerErrorView,
  network: NetworkErrorView,
  generic: GenericErrorView,
};

export function VerifyRoute() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<ErrorInfo | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const code = params.get("code");
    const oauthError = params.get("error");

    if (oauthError === "access_denied") {
      setError({ kind: "access_denied", raw: oauthError });
      return;
    }

    if (!code) {
      setError({ kind: "generic", raw: "No authorization code received from Google." });
      return;
    }

    api
      .googleAuth(code, googleRedirectUri())
      .then(({ token }) => {
        setStoredToken(token);
        navigate("/candidates", { replace: true });
      })
      .catch((err: Error) => {
        const raw = err.message || "Google sign-in failed.";
        setError({ kind: classifyError(raw), raw });
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    const ErrorView = ERROR_VIEWS[error.kind];
    return <ErrorView />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-secondary">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-border-card border-t-rausch" />
        <p className="text-sm text-text-secondary">Verifying your Google account…</p>
      </div>
    </div>
  );
}
